import React, { useCallback, useEffect, useState } from 'react';
import { Loader } from 'arag-ui-lib';
import {
    SurveyViews,
    confirmationSurveyCode,
} from '../../utils/constants/surveyViews';
import * as actions from '../../actions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { useHistory } from 'react-router-dom';
import questionProjections from '../../projections/question';
import SurveyProjections from '../../projections/survey';
import SurveyConfirmation from '../../components/surveyConfirmation/surveyConfirmation';
import questionFunctions from '../../utils/functions/question';
import _ from 'lodash';
import SingleSurvey from '../../components/survey/singleSurvey';
import MultipleSurvey from '../../components/survey/multipleSurvey';
import codes from '../../utils/constants/codes';
import { useSelector } from 'react-redux';
import ids from '../../utils/constants/ids';
import { ObjectOfAny, QuestionSchema } from '../../reducers/types';
import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import { useTranslation } from 'react-i18next';

//Views are handle with history & location state of react router inside the survey tab
type Props = {
    claimApplication: any;
};

const SurveyContainer = (props: Props) => {
    const { claimApplication } = props;
    const history = useHistory();
    const dispatch = useAppDispatch();
    const [activeView, setActiveView] = useState<any>(null);
    const [hasUncompletedSurvey, setHasUncompletedSurvey] = useState(
        null,
    ) as any;
    const surveyData = useAppSelector<any>(SurveyProjections.surveyData);
    const questions: any = useSelector(
        questionProjections.surveyQuestions(
            activeView?.surveyTypeGroupCode
                ? codes.surveysGroupCodesToGroupIds[
                      activeView?.surveyTypeGroupCode
                  ]
                : null,
        ),
    );
    const { t } = useTranslation();
    const fetchQuestionTypeAction = useCallback(
        () => dispatch(actions.fetchQuestionTypes()),
        [dispatch],
    );
    const fetchSurveyTypeAction = useCallback(
        (payload: actions.fetchSurveyTypeAction) =>
            dispatch(actions.fetchSurveyType(payload)),
        [dispatch],
    );
    const fetchQuestionsByGroupAction = useCallback(
        (payload: any) => dispatch(actions.fetchQuestionsByGroup(payload)),
        [dispatch],
    );
    const postSurveyAnswersAction = useCallback(
        (payload: any) => dispatch(actions.postSurveyAnswers(payload as any)),
        [dispatch],
    );

    useEffect(() => {
        fetchQuestionTypeAction();
        fetchSurveyTypeAction({
            claimApplicationId: claimApplication.id,
        });
    }, [claimApplication.id]);

    useEffect(() => {
        if (surveyData.surveyTypes.length === 1 && !activeView) {
            setActiveView(
                SurveyViews[surveyData.surveyTypes[0]?.surveyTypeName],
            );
        }
        if (
            surveyData.surveyTypes.length > 1 &&
            activeView?.surveyTypeId !== confirmationSurveyCode
        ) {
            setActiveView(null);
        }
        let hasNextSurveys = surveyData.surveyTypes?.find(
            (survey: any) => !survey.completed,
        );
        if (hasNextSurveys) {
            setHasUncompletedSurvey(true);
        } else {
            setHasUncompletedSurvey(false);
        }
    }, [surveyData.surveyTypes]);

    //helper for questions
    const questionsSurveyHandler = (item: {
        surveyTypeId: string;
        surveyTypeName: string;
        surveyTypeGroupCode: string;
    }) => {
        fetchQuestionsByGroupAction({
            claimApplicationId: claimApplication.id,
            groupCode: item.surveyTypeGroupCode,
        });
        history.push({
            pathname: history.location.pathname,
            search: history.location.search,
            state: item.surveyTypeName,
        });
    };

    //helper for surveys
    const surveysHandler = (item: {
        surveyTypeId: string;
        surveyTypeName: string;
        surveyTypeGroupCode: string;
    }) => {
        questionsSurveyHandler(item);
        const updatedSurveyState = {
            [item.surveyTypeName]: {
                ...SurveyViews[item.surveyTypeName],
                surveyGuidId: item.surveyTypeId,
            },
        };
        setActiveView(updatedSurveyState[item.surveyTypeName]);
    };

    // on Submit survey handler
    const onSubmitSurvey = (values: any) => {
        const radioQuestions = questions.questions.filter(
            (question: QuestionSchema) =>
                question.displayType === 'vertical-radio' ||
                question.displayType === 'horizontal-radio',
        );
        let formattedValues = GENERAL_FUNCTIONS.formatValuesForRadioQuestion(
            values,
            radioQuestions,
        ) as ObjectOfAny;
        let filteredQuestions = GENERAL_FUNCTIONS.filterObjectBy(
            questions.questionsById,
            (q: { groupId: string }) =>
                q.groupId ===
                codes.surveysGroupCodesToGroupIds[
                    activeView?.surveyTypeGroupCode
                ],
        );
        const answersArray = questionFunctions.constructAnswersDTO(
            filteredQuestions,
            questions.questionTypes,
            _.pickBy(
                formattedValues,
                (_value: unknown, key: string) => !!filteredQuestions[key],
            ),
            null,
            [],
        );
        postSurveyAnswersAction({
            data: {
                Answers: answersArray,
                claimApplicationId: claimApplication.id,
                surveyTypeGroupCode: activeView.surveyTypeGroupCode,
            },
        });
        setActiveView(SurveyViews[confirmationSurveyCode]);
    };

    //next survey handler
    const navigateToNextSurvey = () => {
        const nextSurvey = surveyData?.surveyTypes.find(
            (survey: any) => !survey.completed,
        );
        if (nextSurvey) {
            questionsSurveyHandler(nextSurvey);
            setActiveView(SurveyViews[nextSurvey.surveyTypeName]);
            setHasUncompletedSurvey(null);
        }
    };

    //State: Loading Survey
    if (
        surveyData.loading ||
        (surveyData.surveyTypes?.length === 0 && !surveyData.errorMessage)
    ) {
        return <Loader variant="inline" label={`${t('loading')}`} />;
    }
    // State: Confirmation Survey
    if (activeView?.surveyTypeId === confirmationSurveyCode) {
        return (
            <SurveyConfirmation
                context={surveyData?.postingSuccessBody}
                navigateToNextSurvey={navigateToNextSurvey}
                surveyTypes={surveyData?.surveyTypes}
                hasUncompletedSurvey={hasUncompletedSurvey}
            />
        );
    }
    // State: Error Mock Survey
    if (surveyData?.errorMessage) {
        return (
            <SurveyConfirmation
                context={surveyData?.errorMessage}
                navigateToNextSurvey={navigateToNextSurvey}
                hasUncompletedSurvey={hasUncompletedSurvey}
            />
        );
    }

    // State: Single or Multiple Survey
    return (
        <>
            <div>
                {surveyData.surveyTypes.length === 1 ? (
                    <SingleSurvey
                        questions={questions}
                        onSubmitSurvey={onSubmitSurvey}
                        posting={surveyData.posting}
                        heading={surveyData.surveyTypes[0]?.surveyTypeName}
                    />
                ) : (
                    <MultipleSurvey
                        surveyData={surveyData}
                        surveysHandler={surveysHandler}
                        activeView={activeView}
                        questions={questions}
                        onSubmitSurvey={onSubmitSurvey}
                        posting={surveyData.posting}
                        heading={SurveyViews[activeView]}
                    />
                )}
            </div>
        </>
    );
};

export default SurveyContainer;
